import { useBreakpoint, Flex, Spinner, SimpleGrid } from "@chakra-ui/react";
import { AxiosResponse } from "axios";
import { useMemo, CSSProperties } from "react";

import { BrowseEmptyState } from "../../screens/browse/components/EmptyState";
import { findElementHeight } from "./measureCardHeight";
import {
  LayoutStyle,
  DisplayFlags,
  ServerListResponse,
  Comic,
  Artist,
  Article,
  Release,
  Bundle,
} from "../../types";
import { ContentCard } from "../cards/ContentCard";
import { sizeEstimator } from "../cards/hooks/useGridCardDimensions";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { WindowVirtualGrid } from "../grid/WindowVirtualizer";

type ResultItem = Comic | Artist | Article | Release | Bundle;

interface ResultsGridProps {
  gridHeightAdjustment: CSSProperties["height"];
  layout: { style: LayoutStyle; display: DisplayFlags };
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  hasNextPage: boolean;
  allPages: AxiosResponse<ServerListResponse<ResultItem>>[];
  columnPadding?: number;
  onScroll?: (scrollableRef: HTMLDivElement) => void;
  header?: React.ReactNode;
  gridContainerRef?: React.RefObject<HTMLDivElement>;
  scrollContainerHeight?: string;
  gridProps?: {
    [key: string]: any;
  };
  gridItemsAlign?: CSSProperties["alignItems"];
  gridItemWidth?: CSSProperties["width"];
  gridItemHeight?: CSSProperties["height"];
  key?: string;
  fallbackMessage?: string | null;
  searchQuery?: string;
  /** Callback for scroll restoration - reports first visible row index */
  onVisibleIndexChange?: (index: number) => void;
  /** Row index to restore scroll to on back navigation */
  restoreToIndex?: number;
  /** Override the breakpoint-based column count (used by FluidVirtualGrid) */
  columnCountOverride?: number;
}

export function ResultsGrid({
  gridHeightAdjustment,
  layout,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  allPages,
  columnPadding = 16,
  onScroll,
  header,
  gridContainerRef,
  gridProps,
  gridItemsAlign,
  gridItemWidth,
  gridItemHeight,
  fallbackMessage,
  searchQuery,
  onVisibleIndexChange,
  restoreToIndex,
  columnCountOverride,
}: ResultsGridProps) {
  const breakpoint = useBreakpoint({ ssr: false });

  const dimensions = useMemo(() => {
    return sizeEstimator(
      layout.style.context,
      layout.style.mode,
      layout.style.size,
    );
  }, [layout]);

  const isSmallScreen = useIsSmallBreakpoint();

  const columnCount = useMemo(() => {
    if (columnCountOverride !== undefined) return columnCountOverride;
    if (layout.style.mode === "list") {
      switch (breakpoint) {
        case "xs":
        case "sm":
          return 1;
        case "md":
        case "lg":
          return 2;
        case "xl":
        case "2xl":
        case "3xl":
          return 3;
        default:
          return 4;
      }
    }
    switch (breakpoint) {
      case "xs":
      case "sm":
        return 2;
      case "md":
      case "lg":
      case "xl":
        return 4;
      case "2xl":
        return 6;
      default:
        return 8;
    }
  }, [breakpoint, layout.style.mode, columnCountOverride]);

  // Flatten all items from all pages into a single array
  const allItems = useMemo(() => {
    return allPages.flatMap((page) => page.data.payload.results);
  }, [allPages]);

  // Group items into rows based on columnCount
  const rows = useMemo(() => {
    const result: ResultItem[][] = [];
    for (let i = 0; i < allItems.length; i += columnCount) {
      result.push(allItems.slice(i, i + columnCount));
    }
    return result;
  }, [allItems, columnCount]);

  const showEmptyState = !isLoading && allItems.length === 0;

  return (
    <Flex
      direction="column"
      gap={2}
      grow={1}
      width="100%"
      ref={gridContainerRef}
      position="relative"
    >
      <WindowVirtualGrid
        onScroll={onScroll}
        header={
          <>
            {header}
            {isLoading && allItems.length === 0 ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                grow={1}
                pt="48px"
              >
                <Spinner color="blaze.blaze" />
              </Flex>
            ) : null}
            {showEmptyState ? <BrowseEmptyState /> : null}
            {fallbackMessage && !isLoading && !showEmptyState && searchQuery ? (
              <Flex
                direction="column"
                px={isSmallScreen ? "16px" : "0px"}
                pt="20px"
                pb="40px"
                alignItems="center"
                width="100%"
                gap="8px"
              >
                <Flex
                  fontFamily="Bricolage Grotesque"
                  color="dune.dune"
                  fontSize="24px"
                  fontWeight="500"
                  lineHeight="130%"
                >
                  No results for
                  <span style={{ fontWeight: "700" }}>
                    &nbsp; "{searchQuery}"
                  </span>
                </Flex>
                <Flex
                  color="neutral.400"
                  fontSize="18px"
                  fontWeight="500"
                  lineHeight="130%"
                >
                  {fallbackMessage}
                </Flex>
              </Flex>
            ) : null}
          </>
        }
        data={rows}
        overscan={3}
        scrollContainerWidth="100%"
        scrollContainerHeight={`calc(100vh - ${gridHeightAdjustment})`}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onFetchNextPage={fetchNextPage}
        renderItem={(rowItems) => (
          <SimpleGrid
            {...(gridProps
              ? gridProps
              : {
                  columns: columnCount,
                  gap: `${columnPadding}px`,
                })}
            alignItems={gridItemsAlign}
            paddingRight={
              layout.style.size === "v2"
                ? isSmallScreen ? "16px" : "24px"
                : isSmallScreen
                  ? layout.style.mode === "list"
                    ? "12px"
                    : undefined
                  : "40px"
            }
            paddingLeft={
              layout.style.size === "v2"
                ? isSmallScreen ? "16px" : undefined
                : isSmallScreen
                  ? layout.style.mode === "list"
                    ? "12px"
                    : undefined
                  : undefined
            }
            opacity={isLoading ? 0.5 : 1}
            transition="opacity 0.2s"
            py={layout.style.size === "v2" ? "8px" : undefined}
          >
            {rowItems.map((item, i) => (
              <Flex key={item?.id ?? i} width="100%" justifyContent="center">
                <Flex
                  width={
                    gridItemWidth ??
                    (layout.style.mode === "list"
                      ? "100%"
                      : `${dimensions.width}px`)
                  }
                  height={gridItemHeight}
                >
                  {item ? (
                    <ContentCard
                      content={item}
                      layout={layout}
                      index={i}
                      fullWidth={
                        layout.style.mode === "list" ||
                        layout.style.context === "bundle" ||
                        layout.style.size === "v2"
                      }
                    />
                  ) : null}
                </Flex>
              </Flex>
            ))}
          </SimpleGrid>
        )}
        totalCount={rows.length}
        direction="vertical"
        estimateSize={() => dimensions.height + (isSmallScreen ? 16 : 24)}
        measureElement={(el) =>
          findElementHeight(el, dimensions.height, layout.style.context) +
          (isSmallScreen ? 16 : 24)
        }
        onVisibleIndexChange={onVisibleIndexChange}
        restoreToIndex={restoreToIndex}
      />
    </Flex>
  );
}
